import { sanitizeCanvasHtml } from "../utils/canvas.js";

export interface QuizChoice {
  id: string;
  item_body: string;
}

interface QuizQuestionCardProps {
  index: number;
  entryId: string;
  itemBody: string | undefined;
  choices: QuizChoice[];
  selectedChoiceId: string | undefined;
  onSelect: (choiceId: string) => void;
  /** Disables all choices (e.g. after submitting a graded quiz). */
  disabled?: boolean;
  /**
   * "default" — single-column list layout (quiz.tsx)
   * "grid"    — flex-col card for equal-height grid cells (assessment-quiz.tsx)
   */
  variant?: "default" | "grid";
}

export function QuizQuestionCard({
  index,
  entryId,
  itemBody,
  choices,
  selectedChoiceId,
  onSelect,
  disabled = false,
  variant = "default",
}: QuizQuestionCardProps) {
  const isGrid = variant === "grid";
  const cardClass = isGrid
    ? "relative flex flex-col rounded-xl border border-gray bg-white/40 p-5 shadow-sm"
    : "relative rounded-xl border border-gray bg-white/40 p-5";
  const bodyClass = isGrid
    ? "_isolated-root mb-4 flex-1 font-medium leading-relaxed"
    : "_isolated-root mb-4 font-medium leading-relaxed";
  const choiceListClass = isGrid ? "space-y-2" : "space-y-3";

  return (
    <div className={cardClass}>
      <span className="absolute -top-3 left-4 rounded border border-lac-leman bg-lac-leman px-2 py-0.5 text-xs font-semibold text-white shadow-sm">
        {index + 1}
      </span>
      <div className={bodyClass}>
        <span dangerouslySetInnerHTML={{ __html: sanitizeCanvasHtml(itemBody ?? "") }} />
      </div>
      <div className={choiceListClass}>
        {choices.map(choice => {
          const isSelected = selectedChoiceId === choice.id;
          return (
            <label
              key={choice.id}
              aria-label={choice.item_body.replace(/<[^>]*>/g, "")}
              className={`flex items-start gap-3 rounded-lg border px-4 py-3 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-lac-leman has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-white ${
                disabled ? "cursor-default" : "cursor-pointer hover:bg-lac-leman-8/60"
              } ${isSelected ? "border-lac-leman bg-lac-leman-8/60" : "border-gray"}`}>
              <input
                type="radio"
                name={`${entryId}-${choice.id}`}
                value={choice.id}
                checked={isSelected}
                disabled={disabled}
                onChange={() => onSelect(choice.id)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onSelect(choice.id);
                  }
                }}
                className="mt-0.5 accent-lac-leman"
              />
              <span
                className="text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizeCanvasHtml(choice.item_body) }}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}
